import api from "@/app/api";
import type { BillingStatus } from "@/app/services/generated/billingplanService";
import { billingPlanService } from "@/app/services/generated/billingplanService";
import type { User } from "@/app/services/generated/userService";
import { userService } from "@/app/services/generated/userService";
import type { LicensedUserSession } from "@/app/services/generated/usersessionService";
import { userSessionService } from "@/app/services/generated/usersessionService";
import { adminRoutes } from "@/app/ui/navigation/administrationMenu";
import { makeRef, withLoading } from "@/components/composables/componentTools";
import GridConfigurationBuilder from "@/components/controls/grid/builder/GridConfigurationBuilder";
import BmGridStateRepository from "@/components/controls/grid/state/BmGridStateRepository";
import messages, { keys, translateWithParams } from "@/plugins/i18n";
import { screenSize } from "@/vue23switching/screenSize";
import { useRouter } from "@/router/router";
import userStore from "@/store/modules/userStore";
import type { Instant } from "@js-joda/core";
import { Duration, LocalDateTime } from "@js-joda/core";
import { computed, onBeforeUnmount, onMounted } from "vue";
import type { Maybe } from "@/components/utils/typeHelpers";
import { GuidMap } from "@/app/types/common/guidcollections";
import { toGuidMap } from "@/app/apiutils";

export function useCurrentSessions() {
  const { router } = useRouter();

  const billingStatus = makeRef<Maybe<BillingStatus>>(null);
  const items = makeRef<readonly LicensedUserSession[]>([]);
  const users = makeRef(new GuidMap<User>());
  const gridState = BmGridStateRepository.getEphemeral();
  let refreshTimer = 0;

  const tenantMaxSessions = computed(
    () => billingStatus.value?.permittedConcurrentUsers ?? 0
  );

  const gridConfiguration = computed(() => {
    let maker = new GridConfigurationBuilder<LicensedUserSession>({
      itemId: (item) => item.sessionId,
      title: messages.auth_current_sessions,
    });
    maker.addTextColumn(
      keys.users_name,
      (item) => users.value.get(item.userId)?.name
    );
    maker.addTextColumn(
      keys.users_job_title,
      (item) => users.value.get(item.userId)?.jobTitle
    );

    maker
      .addIntegerColumn(keys.auth_last_activity_time, (item) =>
        minutesSinceActive(item.lastAccess)
      )
      .defaultSort(0, true);

    maker.addBooleanCheckMarkColumn(
      keys.auth_is_current_session,
      (item) => item.isCurrentSession
    );

    maker
      .addActionsColumn()
      .showNarrow(false)
      .withAction(
        messages.auth_end_session,
        (item) => endSession(item),
        (item) => !item.isCurrentSession
      );

    return maker.build();
  });

  const refresh = async () => {
    if (document.visibilityState === "visible") {
      items.value = await userSessionService
        .getAllLicensedUserSessions(api.tenantGroupId)
        .then((x) => Object.freeze(x));
    }

    await performLicenseCheck();
  };

  const performLicenseCheck = async () => {
    // form will automatically be closed once a license is allocated
    await userStore.performLicenseCheck();
  };

  const signOut = async () => {
    await userStore.signOut("current session sign out");
  };

  const endSession = async (item: LicensedUserSession) => {
    await userSessionService.kickLicensedUserSession(
      api.tenantGroupId,
      item.sessionId
    );

    await userStore.performLicenseCheck();
    await refresh();
  };

  const minutesSinceActive = (lastActive: Instant) => {
    return Duration.between(
      LocalDateTime.ofInstant(lastActive),
      LocalDateTime.now()
    ).toMinutes();
  };

  const logoffPromptText = computed(() => {
    return translateWithParams("auth_force_logoff_description", {
      sessionCount: items.value.length.toString(),
      licensedUsersNo: tenantMaxSessions.value.toString(),
    }).toString();
  });

  const isDesktopView = computed(() => {
    return screenSize.smAndUp;
  });

  onMounted(
    withLoading(async () => {
      [users.value, billingStatus.value] = await Promise.all([
        userService
          .getAllUsers(api.tenantGroupId)
          .then((x) => toGuidMap(x, (user) => user.id)),
        billingPlanService.getBillingStatus(api.tenantGroupId),
      ]);

      if (tenantMaxSessions.value) {
        await refresh();
        // Refresh data after 1 Minute ("+" causes a toPrimitive call with "number" hint)
        refreshTimer = +setInterval(refresh, 60000);
      } else {
        // If the user does not have any permitted users then they most likely have
        // a billing issue (or have cancelled) so send them over to billing to inform/resolve.
        await router.push(adminRoutes.billingDetails.location());
      }
    })
  );

  onBeforeUnmount(() => clearInterval(refreshTimer));

  return {
    items,
    tenantMaxSessions,
    gridState,
    gridConfiguration,
    isDesktopView,
    logoffPromptText,
    refresh,
    signOut,
    performLicenseCheck,
  };
}
